// PARITY-0 UI registries: the one shape every small UI registry shares (board settings tabs, the
// Create menu, ⌘K commands, channel-details tabs, Work's sidebar footer). Entries carry an `id`
// and an `order`; a list reads them sorted by order, then id. A second entry with a taken id
// throws (two features never silently fight over one slot); `register` returns the function that
// takes the entry back out (tests, and a feature that unmounts its surface).

export type OrderedEntry = { id: string; order: number };

export type OrderedRegistry<T extends OrderedEntry> = {
  /** Adds the entry; throws when its id is taken. Returns the unregister function. */
  register: (entry: T) => () => void;
  /** Every entry, sorted by `order`, then `id`. */
  list: () => T[];
};

export function orderedRegistry<T extends OrderedEntry>(name: string): OrderedRegistry<T> {
  const entries = new Map<string, T>();
  return {
    register(entry) {
      if (entries.has(entry.id)) throw new Error(`${name} ${entry.id} is already registered`);
      entries.set(entry.id, entry);
      return () => {
        // Only this registration: a later entry under the same id stays.
        if (entries.get(entry.id) === entry) entries.delete(entry.id);
      };
    },
    list: () => [...entries.values()].sort(byOrder),
  };
}

/** Lower `order` first; ties by `id`, so the list never depends on registration order. */
export function byOrder(a: OrderedEntry, b: OrderedEntry): number {
  return a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}
